<script setup lang="ts">
import background from '~/assets/images/backgrounds/to_interests.jpg';

const getImage = useImage();
const { t } = useI18n();

const IMAGES = [
  {
    src: getImage(t('screenshots.0.url'), { format: 'webp' }),
    alt: t('screenshots.0.alt'),
  },
  {
    src: getImage(t('screenshots.1.url'), { format: 'webp' }),
    alt: t('screenshots.1.alt'),
  },
];
</script>

<template>
  <section class="pt-12 pb-16 p-section relative bg-black">
    <NuxtImg
      class="page-image-preview hidden md:block w-full h-full"
      :src="background"
      width="1000"
      height="1236"
      :alt="t('preview_alt')"
      sizes="lg:100vw xl:100vw xxl:100vw"
      format="avif"
      loading="lazy"
    />
    <div
      class="page-overflow-8 section-top-shadow section-bottom-shadow hidden md:block"
    />

    <h2
      data-show-on-visible
      class="lg:container text-2xl md:text-4xl text-800 relative z-2 text-center md:text-left mb-8"
    >
      {{ t('title') }}
    </h2>
    <div
      class="lg:container grid grid-cols-12 gap-6 md:gap-3 relative z-1"
    >
      <div
        class="col-span-12 md:col-span-5 lg:col-span-6 xl:col-span-5 md:py-3 relative"
      >
        <div class="-bottom-4 -top-20 extended-absolute-screen block md:hidden">
          <NuxtImg
            class="page-image-preview w-full h-full"
            :src="background"
            width="1000"
            height="1236"
            :alt="t('preview_alt')"
            sizes="xs:100vw sm:100vw md:100vw"
            format="avif"
            loading="lazy"
          />
          <div
            class="page-overflow-8 section-top-shadow section-bottom-shadow"
          />
        </div>

        <div
          data-show-on-visible
          class="text-700 text-lg xl:text-xl relative z-1 mb-5"
          style="border-radius: 6px"
        >
          {{ t('description') }}
          <ul class="mt-6 mb-8 pl-4 text-sm xl:text-lg !leading-6">
            <li>{{ t('rules.0') }}</li>
            <li>{{ t('rules.1') }}</li>
            <li>{{ t('rules.2') }}</li>
            <li>{{ t('rules.3') }}</li>
          </ul>
          <Button
            as="nuxt-link"
            to="/about-interests"
            rounded
            size="large"
            class="p-section"
          >
            {{ t('find_out_interests') }}
          </Button>
        </div>
      </div>
      <div
        data-show-on-visible='{ "delay": 150, "noDelayOnMobile": 768 }'
        class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 col-span-12 md:col-span-7 lg:col-span-6 xl:col-span-7 gap-4 relative z-1"
      >
        <div
          v-for="(image, index) in IMAGES"
          :key="image.src"
          class="xl:col-span-5"
          :class="{ 'xl:col-start-3': index === 0 }"
        >
          <div
            class="bg-surface-800 shadow-2 rounded-lg p-2 xl:p-3"
            style="border-radius: 6px"
          >
            <Image
              :src="image.src"
              :alt="image.alt"
              class="w-full !block aspect-screenshot"
              preview
              :preview-button-props="{ 'aria-label': t('zoom_image_button') }"
              :pt="{
                image: { class: 'w-full h-full object-cover', loading: 'lazy' },
              }"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<i18n>
en:
  screenshots:
    - alt: The game of interests will help to reveal the desires of a partner
      url: /images/interests-screen/en/ig-1.png
    - alt: Browse matches and find new experiments with your partner
      url: /images/interests-screen/en/ig-2.png
  preview_alt: Reveal your partner's interests before the game
  title: Reveal your partner's interests
  description: If before the game you do not know what you are ready to do together with a partner, do not know his
    interests or want to share your own, but are afraid, then the game of interests will help you.
  rules:
    - Both partners connect their accounts;
    - Both partners answer the same sex preference questions «Yes», «Maybe», «No»;
    - If both partners answered «Yes» or «Maybe», then the question is shown as a match;
    - Any idea that is answered «No» by any of the partners will be hidden.
  find_out_interests: Find out interests
  zoom_image_button: Zoom image

ru:
  screenshots:
    - alt: Игра в интересы поможет раскрыть желания партнера
      url: /images/interests-screen/ru/ig-1.jpg
    - alt: Просматривайте совпадения и находите новые эксперименты со своим партнером
      url: /images/interests-screen/ru/ig-2.jpg
  preview_alt: Раскройте интересы партнера перед игрой
  title: Раскройте интересы партнера перед игрой
  description: Если перед игрой вы не знаете, на что готовы пойти вместе с партнером, не знаете его интересы или хотите
    поделиться своими, но боитесь этого, то игра в интересы поможет вам.
  rules:
    - Оба партнера соединяют свои аккаунты;
    - Оба партнера отвечают на одинаковые секс вопросы о предпочтениях «Да», «Может», «Нет»;
    - Если оба партнера ответили «Да» или «Может», то вопрос показан как совпадение;
    - Любая идея на которую ответил «Нет» любой из партнеров, будет спрятана.
  find_out_interests: Узнать интересы
  zoom_image_button: Увеличить изображение

de:
  screenshots:
    - alt: Wenn Sie mit Interessen spielen, können Sie die Wünsche Ihres Partners offenlegen
      url: /images/interests-screen/en/ig-1.jpg
    - alt: Durchsuchen Sie Matches und entdecken Sie neue Erfahrungen mit Ihrem Partner
      url: /images/interests-screen/en/ig-2.jpg
  preview_alt: Entdecken Sie vor dem Spiel die Interessen Ihres Partners
  title: Entdecken Sie vor dem Spiel die Interessen Ihres Partners
  description: Wenn Sie vor dem Spiel nicht wissen, was Sie mit Ihrem Partner machen möchten, seine Interessen nicht
    kennen oder Ihre eigenen teilen möchten, aber Angst davor haben, dann hilft Ihnen das Spiel der Interessen.
  rules:
    - Beide Partner verbinden ihre Konten;
    - Beide Partner beantworten gleichgeschlechtliche Fragen zu Vorlieben mit „Ja“, „Vielleicht“, „Nein“;
    - Wenn beide Partner mit „Ja“ oder „Kann“ geantwortet haben, wird die Frage als Übereinstimmung angezeigt;
    - Jede Idee, die von einem der Partner mit „Nein“ beantwortet wird, wird ausgeblendet.
  find_out_interests: Finden Sie Interessen heraus
  zoom_image_button: Bild vergrößern

es:
  screenshots:
    - alt: Jugar con intereses ayudará a revelar los deseos de tu pareja
      url: /images/interests-screen/en/ig-1.jpg
    - alt: Explora partidos y descubre nuevas experiencias con tu pareja
      url: /images/interests-screen/en/ig-2.jpg
  preview_alt: Descubre los intereses de tu pareja antes del juego
  title: Descubre los intereses de tu pareja antes del juego
  description: Si antes del juego no sabes lo que estás dispuesto a hacer con tu pareja, no conoces sus intereses o
    quieres compartir los tuyos, pero tienes miedo, entonces el juego de intereses te ayudará.
  rules:
    - Ambos socios conectan sus cuentas;
    - 'Ambos miembros de la pareja responden preguntas del mismo sexo sobre preferencias: “Sí”, “Quizás”, “No”;'
    - Si ambos socios respondieron "Sí" o "Mayo", entonces la pregunta se muestra como coincidente;
    - Cualquier idea a la que cualquiera de los socios responda "No" quedará oculta.
  find_out_interests: Descubra intereses
  zoom_image_button: Ampliar imagen

fr:
  screenshots:
    - alt: Jouer avec les intérêts aidera à révéler les désirs de votre partenaire
      url: /images/interests-screen/en/ig-1.jpg
    - alt: Parcourez les matchs et découvrez de nouvelles expériences avec votre partenaire
      url: /images/interests-screen/en/ig-2.jpg
  preview_alt: Découvrez les intérêts de votre partenaire avant le match
  title: Découvrez les intérêts de votre partenaire avant le match
  description: Si avant le jeu vous ne savez pas ce que vous êtes prêt à faire avec votre partenaire, vous ne connaissez
    pas ses intérêts ou vous souhaitez partager les vôtres, mais en avez peur, alors le jeu des intérêts vous aidera.
  rules:
    - Les deux partenaires connectent leurs comptes ;
    - Les deux partenaires répondent aux questions du même sexe sur les préférences « Oui », « Peut-être », « Non » ;
    - Si les deux partenaires ont répondu « Oui » ou « Peut », alors la question apparaît comme une correspondance ;
    - Toute idée à laquelle l'un ou l'autre des partenaires répondra « Non » sera masquée.
  find_out_interests: Découvrez vos intérêts
  zoom_image_button: Agrandir l'image
</i18n>
